import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    VStack,
    HStack,
    Text,
    Input,
    Button,
    IconButton,
    Flex,
    useColorModeValue,
    useToast,
    Spinner,
    Center,
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    Container,
    Badge,
    Textarea,
    useDisclosure
} from '@chakra-ui/react';
import {
    ArrowBackIcon,
    ChevronRightIcon,
    SettingsIcon,
    DeleteIcon
} from '@chakra-ui/icons';
import { FiSend } from 'react-icons/fi';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Persona, ChatMessage, ConversationSession } from '../types';
import personaServices from '../services/PersonaServices';
import sessionManager from '../utils/SessionManager';
import { ChatClient } from '../utils/ChatClient';
import MarkdownRenderer from './generated/MarkdownRenderer';
import { generateSystemMessage, capitalizeFirstChar } from '../utils/systemMessageGenerator';

const ConversationInterface = () => {
    const { personaId, sessionId } = useParams<{ personaId: string; sessionId: string }>();
    const [searchParams] = useSearchParams();
    const [persona, setPersona] = useState<Persona | null>(null);
    const [session, setSession] = useState<ConversationSession | null>(null);
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [inputMessage, setInputMessage] = useState('');
    const [isStreaming, setIsStreaming] = useState(false);
    const [streamingContent, setStreamingContent] = useState('');
    const [loading, setLoading] = useState(true);
    
    const navigate = useNavigate();
    const toast = useToast();
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const chatClient = new ChatClient();

    const bg = useColorModeValue('gray.50', 'gray.900');
    const cardBg = useColorModeValue('white', 'gray.800');
    const borderColor = useColorModeValue('gray.200', 'gray.600');
    const userMsgBg = useColorModeValue('blue.500', 'blue.600');
    const assistantMsgBg = useColorModeValue('gray.100', 'gray.700');

    useEffect(() => {
        const loadData = async () => {
            if (!personaId || !sessionId) return;

            try {
                setLoading(true);
                
                const bootstrapVersion = searchParams.get('bootstrap_version') || '3';
                const [personaData, sessionData] = await Promise.all([
                    personaServices.getPersona(personaId, bootstrapVersion),
                    Promise.resolve(sessionManager.getSession(personaId, sessionId))
                ]);

                setPersona(personaData);
                
                if (sessionData) {
                    setSession(sessionData);
                    setMessages(sessionData.messages);
                } else {
                    toast({
                        title: 'Session not found',
                        description: 'The requested session could not be found.',
                        status: 'error',
                        duration: 3000,
                        isClosable: true,
                    });
                    navigate(`/persona/${personaId}`);
                    return;
                }
            } catch (error) {
                console.error('Error loading data:', error);
                toast({
                    title: 'Error',
                    description: 'Failed to load conversation data',
                    status: 'error',
                    duration: 3000,
                    isClosable: true,
                });
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, [personaId, sessionId, navigate, toast]);

    useEffect(() => {
        scrollToBottom();
    }, [messages, streamingContent]);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    const getPersonaSystemMessage = (persona: Persona): string => {
        if (persona.bootstrap_version === '3') {
            return generateSystemMessage(persona);
        }
        return persona.systemMessage || 'You are a helpful assistant.';
    };

    const getPersonaProvider = (persona: Persona): string => {
        if (!persona.provider || persona.provider.trim() === '') {
            // Random selection between groq and cerebras for v3 personas
            const providers = ['groq', 'cerebras'];
            return providers[Math.floor(Math.random() * providers.length)];
        }
        return persona.provider;
    };

    const getPersonaModel = (persona: Persona, provider: string): string => {
        if (!persona.modelName || persona.modelName.trim() === '') {
            // Default models based on provider
            return provider === 'groq' ? 'llama-3.3-70b-versatile' : 'llama-3.3-70b';
        }
        return persona.modelName;
    };

    const handleSendMessage = async () => {
        if (!inputMessage.trim() || !persona || !sessionId || !personaId || isStreaming) return;

        const userMessage: ChatMessage = {
            role: 'user',
            content: inputMessage.trim()
        };

        const updatedMessages = [...messages, userMessage];
        setMessages(updatedMessages);
        sessionManager.saveMessage(personaId, sessionId, userMessage);
        setInputMessage('');
        setIsStreaming(true);
        setStreamingContent('');

        try {
            const systemMessage = getPersonaSystemMessage(persona);
            const provider = getPersonaProvider(persona);
            const model = getPersonaModel(persona, provider);

            console.log('Sending chat with:', { provider, model, systemMessage: systemMessage.substring(0, 100) + '...' });

            // Don't include system message in messages array since it's passed via options
            const messagesToSend: ChatMessage[] = [...updatedMessages];

            const stream = await chatClient.chatCompletion(messagesToSend, {
                model: model,
                provider: provider,
                persona_id: persona.id,
                system_message: systemMessage
            });

            if (stream) {
                const reader = stream.getReader();
                const decoder = new TextDecoder();
                let fullResponse = '';

                while (true) {
                    const { done, value } = await reader.read();
                    if (done) break;

                    const chunk = decoder.decode(value);
                    const lines = chunk.split('\n');

                    for (const line of lines) {
                        if (line.startsWith('data: ')) {
                            const data = line.slice(6);
                            if (data === '[DONE]') continue;
                            if (data.trim() === '') continue;

                            try {
                                const parsed = JSON.parse(data);
                                console.log('Parsed streaming data:', parsed);
                                if (parsed.choices && parsed.choices[0] && parsed.choices[0].delta) {
                                    const content = parsed.choices[0].delta.content || '';
                                    if (content) {
                                        fullResponse += content;
                                        setStreamingContent(fullResponse);
                                        console.log('Updated streaming content:', content);
                                    }
                                } else {
                                    console.warn('Unexpected streaming data format:', parsed);
                                }
                            } catch (e) {
                                console.warn('Failed to parse streaming data:', data, e);
                            }
                        }
                    }
                }

                if (fullResponse) {
                    const assistantMessage: ChatMessage = {
                        role: 'assistant',
                        content: fullResponse
                    };
                    
                    const finalMessages = [...updatedMessages, assistantMessage];
                    setMessages(finalMessages);
                    sessionManager.saveMessage(personaId, sessionId, assistantMessage);
                }
            }
        } catch (error) {
            console.error('Error sending message:', error);
            toast({
                title: 'Error',
                description: 'Failed to send message. Please try again.',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setIsStreaming(false);
            setStreamingContent('');
        }
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    const formatTimestamp = (timestamp: number) => {
        return new Date(timestamp).toLocaleString();
    };

    if (loading) {
        return (
            <Container maxW="container.xl" h="100vh">
                <Center h="100%">
                    <VStack spacing={4}>
                        <Spinner size="xl" color="blue.500" />
                        <Text>Loading conversation...</Text>
                    </VStack>
                </Center>
            </Container>
        );
    }

    if (!persona || !session) {
        return (
            <Container maxW="container.xl" h="100vh">
                <Center h="100%">
                    <Text fontSize="lg" color="red.500">
                        Conversation not found
                    </Text>
                </Center>
            </Container>
        );
    }

    return (
        <Box h="100vh" bg={bg} display="flex" flexDirection="column">
            {/* Header */}
            <Box bg={cardBg} borderBottomWidth="1px" borderColor={borderColor} px={4} py={3}>
                <Container maxW="container.xl">
                    <Flex justify="space-between" align="center">
                        <HStack spacing={4}>
                            <IconButton
                                icon={<ArrowBackIcon />}
                                aria-label="Back to sessions"
                                variant="ghost"
                                onClick={() => navigate(`/persona/${personaId}`)}
                            />
                            <VStack align="start" spacing={1}>
                                <Breadcrumb spacing="8px" separator={<ChevronRightIcon color="gray.500" />}>
                                    <BreadcrumbItem>
                                        <BreadcrumbLink onClick={() => navigate('/')}>
                                            Personas
                                        </BreadcrumbLink>
                                    </BreadcrumbItem>
                                    <BreadcrumbItem>
                                        <BreadcrumbLink onClick={() => navigate(`/persona/${personaId}`)}>
                                            {capitalizeFirstChar(persona.name)}
                                        </BreadcrumbLink>
                                    </BreadcrumbItem>
                                    <BreadcrumbItem isCurrentPage>
                                        <BreadcrumbLink>{session.metadata.title}</BreadcrumbLink>
                                    </BreadcrumbItem>
                                </Breadcrumb>
                                <HStack spacing={2}>
                                    <Badge colorScheme="blue" variant="subtle" fontSize="xs">
                                        {persona.provider}
                                    </Badge>
                                    <Badge colorScheme="green" variant="subtle" fontSize="xs">
                                        {session.metadata.message_count} messages
                                    </Badge>
                                    <Text fontSize="xs" color="gray.500">
                                        {formatTimestamp(session.metadata.updated_at)}
                                    </Text>
                                </HStack>
                            </VStack>
                        </HStack>
                        <HStack>
                            <IconButton
                                icon={<SettingsIcon />}
                                aria-label="Session settings"
                                variant="ghost"
                                size="sm"
                            />
                        </HStack>
                    </Flex>
                </Container>
            </Box>

            {/* Messages */}
            <Box flex={1} overflow="hidden">
                <Container maxW="container.xl" h="100%">
                    <VStack
                        spacing={4}
                        align="stretch"
                        p={4}
                        h="100%"
                        overflowY="auto"
                    >
                        {messages.length === 0 ? (
                            <Center flex={1}>
                                <VStack spacing={4}>
                                    <Text fontSize="lg" color="gray.500">
                                        Start a conversation with {capitalizeFirstChar(persona.name)}
                                    </Text>
                                    <Text fontSize="sm" color="gray.400" textAlign="center" maxW="md">
                                        {persona.desc || 'No description available'}
                                    </Text>
                                </VStack>
                            </Center>
                        ) : (
                            <>
                                {messages.map((message, index) => (
                                    <Flex
                                        key={index}
                                        justify={message.role === 'user' ? 'flex-end' : 'flex-start'}
                                    >
                                        <Box
                                            maxW="70%"
                                            bg={message.role === 'user' ? userMsgBg : assistantMsgBg}
                                            color={message.role === 'user' ? 'white' : 'inherit'}
                                            px={4}
                                            py={3}
                                            borderRadius="lg"
                                            borderBottomRightRadius={message.role === 'user' ? 'sm' : 'lg'}
                                            borderBottomLeftRadius={message.role === 'assistant' ? 'sm' : 'lg'}
                                        >
                                            {message.role === 'assistant' ? (
                                                <MarkdownRenderer content={message.content} />
                                            ) : (
                                                <Text whiteSpace="pre-wrap">{message.content}</Text>
                                            )}
                                        </Box>
                                    </Flex>
                                ))}
                                
                                {isStreaming && streamingContent && (
                                    <Flex justify="flex-start">
                                        <Box
                                            maxW="70%"
                                            bg={assistantMsgBg}
                                            px={4}
                                            py={3}
                                            borderRadius="lg"
                                            borderBottomLeftRadius="sm"
                                        >
                                            <MarkdownRenderer content={streamingContent} />
                                        </Box>
                                    </Flex>
                                )}
                                
                                {isStreaming && !streamingContent && (
                                    <Flex justify="flex-start">
                                        <Box
                                            bg={assistantMsgBg}
                                            px={4}
                                            py={3}
                                            borderRadius="lg"
                                            borderBottomLeftRadius="sm"
                                        >
                                            <HStack spacing={2}>
                                                <Spinner size="sm" />
                                                <Text fontSize="sm" color="gray.500">
                                                    {persona.name} is thinking...
                                                </Text>
                                            </HStack>
                                        </Box>
                                    </Flex>
                                )}
                            </>
                        )}
                        <div ref={messagesEndRef} />
                    </VStack>
                </Container>
            </Box>

            {/* Input */}
            <Box bg={cardBg} borderTopWidth="1px" borderColor={borderColor} p={4}>
                <Container maxW="container.xl">
                    <HStack spacing={3}>
                        <Textarea
                            value={inputMessage}
                            onChange={(e) => setInputMessage(e.target.value)}
                            onKeyPress={handleKeyPress}
                            placeholder={`Message ${persona.name}...`}
                            resize="none"
                            minH="40px"
                            maxH="120px"
                            isDisabled={isStreaming}
                        />
                        <Button
                            leftIcon={<FiSend />}
                            colorScheme="blue"
                            onClick={handleSendMessage}
                            isLoading={isStreaming}
                            loadingText="Sending"
                            isDisabled={!inputMessage.trim() || isStreaming}
                        >
                            Send
                        </Button>
                    </HStack>
                </Container>
            </Box>
        </Box>
    );
};

export default ConversationInterface;